import React, { ReactElement } from 'react';
import { FormikContextType } from 'formik';
import {
  BodyText,
  SpruceHelpInlineLink,
  Button,
  DateInput,
  Grid,
  ISpruceFieldProps,
  NumberInput,
  Select,
  SpruceField,
  TextInput,
  SwitchDialogWindows,
  ModalWindow,
  IWrappedField,
  FlexRow,
  ISelectOption,
} from 'ui-toolkit';
import {
  createJournalEntryPaymentMethodOptions,
  eJournalEntryPaymentMethod,
  IPaymentMethodEntryTypeValue,
} from '../../types/accounting/eJournalEntryPaymentMethod';
import { journalEntryTypeOptions } from '../../types/accounting/eJournalEntryType';
import styled from 'styled-components';
import { IOrderOption } from '../../types/accounting/IOrderOption';
import { IEscrowFileData } from '../../types/accounting/data/IEscrowFileData';
import { OrderSelectHintText } from './OrderSelectHintText';
import { OrderSelectField } from '../shared/OrderSelect/OrderSelectField';
import { IFetchStatus } from 'types/rest/IFetchStatus';

interface IEditJournalEntryModalProps {
  title: string | ReactElement;
  errorTitle: string | ReactElement;
  loadingTitle: string | ReactElement;
  successTitle: string | ReactElement;
  saveButtonText: string;
  disabledFields: string[];
  escrowFile?: IEscrowFileData;
  escrowFileError?: Error;
  fetchStatus: IFetchStatus;
  isValid: boolean;
  onClose: () => void;
  onOrderSelected?: (order?: IOrderOption) => void;
  onSuccess: () => void;
  selectedOrder?: IOrderOption;
  submitForm: () => void;
}

const SpannedSelect = styled(Select)`
  grid-column: 1 / span 2;
`;

export const EditJournalEntryModal: React.FC<IEditJournalEntryModalProps> = ({
  title,
  errorTitle,
  loadingTitle,
  successTitle,
  saveButtonText,
  disabledFields,
  escrowFile,
  escrowFileError,
  fetchStatus,
  isValid,
  onClose,
  onOrderSelected,
  onSuccess,
  selectedOrder,
  submitForm,
}) => {
  const today = new Date().toISOString();

  const entryTypeInferredFromPaymentType = (paymentMethod: eJournalEntryPaymentMethod): boolean => {
    const option = createJournalEntryPaymentMethodOptions.find(
      (option) => option.value.paymentMethod === paymentMethod
    );
    return !!(option && option.value.entryType);
  };

  const findPaymentMethodValue = (field: IWrappedField, formik: FormikContextType<any>) => {
    if (field.value && formik.values.entryType) {
      return createJournalEntryPaymentMethodOptions.find(
        (value) =>
          // the payment method matches, and
          formik.values.paymentMethod === value.value.paymentMethod &&
          // if the payment method has an associated entry type, the entry type matches
          (!value.value.entryType || formik.values.entryType === value.value.entryType)
      );
    }
    return createJournalEntryPaymentMethodOptions.find((value) => field.value === value.value);
  };

  return (
    <SwitchDialogWindows
      errorMessage={
        <BodyText>
          Please <SpruceHelpInlineLink /> to submit a request for help.
        </BodyText>
      }
      errorTitle={errorTitle}
      isLoading={fetchStatus.fetching}
      isCompleted={fetchStatus.fetched}
      isError={fetchStatus.fetched && !fetchStatus.success}
      onCloseClick={() => {
        if (!fetchStatus.fetching) {
          if (fetchStatus.fetched && fetchStatus.success) onSuccess();
          onClose();
        }
      }}
      loadingTitle={loadingTitle}
      successTitle={successTitle}
    >
      <ModalWindow
        contentJustifyContent={'start'}
        contentTextAlign={'left'}
        minHeight={400}
        onClose={onClose}
        title={title}
        footer={
          <FlexRow justifyContent={'flex-end'}>
            <Button intent={'ghost'} onClick={onClose}>
              Cancel
            </Button>
            <Button intent={'primary'} disabled={!isValid || !!escrowFileError} onClick={submitForm} type="submit">
              {saveButtonText}
            </Button>
          </FlexRow>
        }
        uitype="fit"
        width={580}
      >
        <Grid columnCount={2} columnWidth="1fr">
          <SpruceField name="paymentMethod" inputType="select">
            {({ field, formik }: ISpruceFieldProps) => (
              <SpannedSelect
                {...field}
                inputId="paymentMethod"
                isClearable
                label="Payment Method*"
                menuPlacement="auto"
                menuPosition="fixed"
                onChange={(value?: ISelectOption<IPaymentMethodEntryTypeValue>) => {
                  if (value) {
                    const entryType = value.value.entryType;
                    const paymentMethod = value.value.paymentMethod;
                    if (entryType) formik.setFieldValue('entryType', entryType, true);
                    field.onChange(value);
                    formik.setFieldValue('paymentMethod', paymentMethod, true);
                  } else field.onChange(value);
                }}
                options={createJournalEntryPaymentMethodOptions}
                placeholder="Select a Payment Method"
                value={findPaymentMethodValue(field, formik)}
              />
            )}
          </SpruceField>
          <OrderSelectField
            name="orderId"
            label="File Number*"
            placeholder="Enter File Number"
            isClearable
            isDisabled={disabledFields.includes('escrowFileId')}
            hintText={
              selectedOrder && (
                <OrderSelectHintText selectedOption={selectedOrder} escrowFile={escrowFile} error={escrowFileError} />
              )
            }
            onOptionSet={(orderOption?: IOrderOption) => {
              if (onOrderSelected) onOrderSelected(orderOption);
            }}
          />
          <SpruceField name="amountCents">
            {({ field }: ISpruceFieldProps) => (
              <NumberInput {...field} label="Amount*" placeholder="0.00" mode="currency" valueIsCents={true} />
            )}
          </SpruceField>
          <SpruceField name="entryType" inputType="select">
            {({ field, formik }: ISpruceFieldProps) => (
              <Select
                {...field}
                inputId="entryType"
                isClearable
                label="Entry Type*"
                menuPlacement="auto"
                menuPosition="fixed"
                options={journalEntryTypeOptions}
                isDisabled={
                  formik.values.paymentMethod && entryTypeInferredFromPaymentType(formik.values.paymentMethod)
                }
                value={journalEntryTypeOptions.find((value) => value.value === field.value)}
              />
            )}
          </SpruceField>
          <SpruceField name="note">
            {({ field }: ISpruceFieldProps) => <TextInput {...field} label="Note*" placeholder="Enter any notes" />}
          </SpruceField>
          <SpruceField name="initiatedAtDate">
            {({ field }: ISpruceFieldProps) => (
              <DateInput
                {...field}
                value={field.value}
                onChange={(value) => field.onChange(value.value)}
                label="Initiated Date*"
                placeholder="Select a Date"
                maxDate={today}
              />
            )}
          </SpruceField>
          <SpruceField name="completedAtDate">
            {({ field }: ISpruceFieldProps) => (
              <DateInput
                {...field}
                value={field.value}
                onChange={(value) => field.onChange(value.value)}
                label="Completed Date"
                placeholder="Select a Date"
                maxDate={today}
              />
            )}
          </SpruceField>
        </Grid>
      </ModalWindow>
    </SwitchDialogWindows>
  );
};
